import React, {useEffect, useState} from 'react';
import {
    Option,
    Select
} from '@deere/fuel-react';
import {Button} from '@mui/material';
import {useOktaAuth} from '@okta/okta-react';
import {useTranslation} from 'react-i18next';
import {Add, Delete} from '@mui/icons-material';

interface Instructor {
    instructorId: string;
    instructorName: string;
    domainId: string;
    dealership: string;
}

interface InstructorSelectProps {
    courseId: string;
    courseTitle: string;
    courseType: string;
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    secondaryInstructors?: { [key: string]: string };
    handleSecondaryInstructorChange?: (value: { [key: string]: string }) => void;
    sessionData: any;
    maxSecondaryInstructors: number;
    addRemoveInstructor: (session: string, type: string) => void;
    buttonIndex: number;
    setInstructorsData: (instructors: Instructor[]) => void;
    instructors: any;
    userType: string;
}

const InstructorSelect: React.FC<InstructorSelectProps> = ({
    courseId,
    courseTitle,
    courseType,
    name,
    value,
    onChange,
    placeholder = ' ',
    required,
    disabled,
    secondaryInstructors = {},
    handleSecondaryInstructorChange,
    sessionData,
    setInstructorsData,
    maxSecondaryInstructors,
    addRemoveInstructor,
    buttonIndex,
    instructors,
    userType
}) => {
    const [prevCourseId, setPrevCourseId] = useState<string | null>(courseId);
    const [prevUserType, setPrevUserType] = useState<string | null>(userType);
    const {authState} = useOktaAuth();
    const {t} = useTranslation();
    const isAnySecondaryInstructorSelected = (): boolean => {
        return Object.values(sessionData.secondaryInstructor).some((val) => val);
    };
    const isAnySecondaryInstructorDropdownPresent = (): boolean => {
        return Object.keys(sessionData.secondaryInstructor).length > 0;
    };

    useEffect(() => {
        if (courseId !== prevCourseId || userType !== prevUserType) {
            fetch(`${process.env.REACT_APP_BACKEND_URL}/dealerinstructors?courseId=${courseId}&courseType=${courseType}&userType=${userType}`, {
                headers: {
                    method: 'GET',
                    'Authorization': `Bearer ${authState?.accessToken?.accessToken}`
                }
            }).then((res) => res.json())
                .then((data: Instructor[]) => {
                    setInstructorsData(data);
                    return data;
                }).catch((error) => {
                    console.log(error);
                });
            setPrevCourseId(courseId);
            setPrevUserType(userType);
        }
    }, [authState?.accessToken?.accessToken, courseId, prevCourseId, prevUserType, setInstructorsData, courseType, courseTitle, userType]);

    return (
        <>
            <Select
                disabled={disabled}
                hasSearch
                name={name}
                onChange={onChange}
                placeholder={placeholder}
                required={required}
                value={value}
            >
                {instructors.map((inst: any) => (
                    <Option
                        key={inst.instructorId}
                        value={JSON.stringify({
                            instructorId: inst.instructorId,
                            instructorName: inst.instructorName,
                            domainId: inst.domainId,
                            dealership: inst.dealership
                        })}
                    >
                        {inst.instructorName}
                    </Option>
                ))}
            </Select>
            {Object.keys(secondaryInstructors).map((key, index) => {
                const selectedSecondaryInstructors = [
                    value,
                    ...Object.values(secondaryInstructors).slice(0, index)
                ];

                const filteredInstructors = instructors.filter((inst: any) => !selectedSecondaryInstructors.includes(JSON.stringify({
                    instructorId: inst.instructorId,
                    instructorName: inst.instructorName,
                    domainId: inst.domainId,
                    dealership: inst.dealership
                })));

                return (
                    <div key={key}>
                        <p className='optional-field'>{t('requestClass.secondaryInstructor')}</p>
                        <Select
                            disabled={!value || filteredInstructors.length === 0}
                            hasSearch
                            name={key}
                            onChange={(e): void => handleSecondaryInstructorChange?.({
                                [key]: e.target.value
                            })}
                            placeholder=' '
                            required={required}
                            value={secondaryInstructors[key]}
                        >
                            {filteredInstructors.map((inst: any) => (
                                <Option
                                    key={inst.instructorId}
                                    value={inst.instructorId}
                                >
                                    {inst.instructorName}
                                </Option>
                            ))}
                        </Select>
                    </div>
                );
            })}
            {Object.keys(secondaryInstructors).length < maxSecondaryInstructors && (<Button
                className='add-instructor-button'
                disabled={isAnySecondaryInstructorDropdownPresent() && !isAnySecondaryInstructorSelected()}
                onClick={(): void => addRemoveInstructor(`session-${buttonIndex + 1}`, 'add')}
                startIcon={<Add/>}
                variant={'text-inline'}
            >
                {t('requestClass.addInstructor')}
            </Button>)}
            <Button
                className='add-instructor-button'
                disabled={!isAnySecondaryInstructorDropdownPresent()}
                onClick={(): void => addRemoveInstructor(`session-${buttonIndex + 1}`, 'remove')}
                startIcon={<Delete/>}
                variant={'text-inline'}
            >
                {t('requestClass.removeInstructor')}
            </Button>
        </>
    );
};

export default InstructorSelect;
